import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { AdquisicionReactivosViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-adquisicion-reactivos-autocomplete",
    templateUrl: "./adquisicion-reactivos-autocomplete.component.html",
    styleUrls: ["./adquisicion-reactivos-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AdquisicionReactivosAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class AdquisicionReactivosAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Adquisición Reactivos";
	@Input() placeholder =
		"Buscar adquisición de reactivos por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint =
		"Busque por código o descripción de adquisición de reactivos";

	// Outputs
	@Output() adquisicionReactivosSelected =
		new EventEmitter<AdquisicionReactivosViewModel>();

	// State
	adquisicionesReactivos: AdquisicionReactivosViewModel[] = [];
	adquisicionReactivosId: number | null = null;
	selectedAdquisicionReactivos: AdquisicionReactivosViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadAdquisicionesReactivos();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.adquisicionReactivosId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de adquisiciones de reactivos
	 */
	private loadAdquisicionesReactivos(): void {
		this.masterService
			.getAdquisicionReactivosList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (adquisicionesReactivos) => {
					this.adquisicionesReactivos = adquisicionesReactivos;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar las adquisiciones de reactivos",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedAdquisicionReactivos =
			this.adquisicionReactivosId != null
				? (this.adquisicionesReactivos.find(
						(a) => a.id === this.adquisicionReactivosId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para una adquisición de reactivos
	 */
	displayAdquisicionReactivos = (
		adquisicionReactivos: AdquisicionReactivosViewModel | string,
	): string => {
		if (typeof adquisicionReactivos === "string") {
			return adquisicionReactivos;
		}
		return adquisicionReactivos &&
			adquisicionReactivos.codigo &&
			adquisicionReactivos.descripcion
			? `${adquisicionReactivos.codigo} - ${adquisicionReactivos.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(
		adquisicionReactivos: AdquisicionReactivosViewModel | null,
	): void {
		this.adquisicionReactivosId = adquisicionReactivos
			? adquisicionReactivos.id
			: null;
		this.selectedAdquisicionReactivos = adquisicionReactivos;
		this.onChange(this.adquisicionReactivosId);
		this.onTouched();
		if (adquisicionReactivos) {
			this.adquisicionReactivosSelected.emit(adquisicionReactivos);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
